import React from 'react'
import { List, useTable, ShowButton, DateField } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space, Tabs } from 'antd'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { renderAvatarName } from '../../theme/columns'
import { Clock, RefreshCw, BadgeCheck, Ban } from '../../components/icons'
import { statusTone, statusLabel, typeLabel } from './decision'

export interface Business {
  id: string
  name: string
  tagline: string | null
  description: string | null
  category_slug: string
  business_type: string
  address: string | null
  city: string | null
  state: string | null
  service_areas: string[]
  service_area_note: string | null
  hours: string | null
  phone_e164: string | null
  whatsapp_e164: string | null
  website_url: string | null
  cover_url: string | null
  photo_urls: string[]
  is_owner_attested: boolean
  is_mlm: boolean
  accepts_requests: boolean
  status: string
  submitted_at: string
  moderation_status: string
  decision_note: string | null
  // FK embed — the owner's profile, so the queue never shows a bare uuid.
  owner?: { name: string | null; class_year: string | null; avatar_url: string | null } | null
}

/** Owner profile pulled inline via the named profiles FK (no second round trip). */
export const SELECT =
  '*, owner:profiles!businesses_owner_profile_fkey(name,class_year,avatar_url)'

const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'changes_requested', label: 'Changes requested' },
  { key: 'approved', label: 'Live' },
  { key: 'paused', label: 'Paused' },
  { key: 'rejected', label: 'Not approved' },
  { key: 'removed', label: 'Removed' },
  { key: 'all', label: 'All' },
]

/** Checks column: photo-moderation pill plus an optional Distributor pill. */
function ChecksCell({ record }: { record: Business }) {
  let modPill: React.ReactNode
  if (record.moderation_status === 'approved') {
    modPill = <Pill tone="green">Photos OK</Pill>
  } else if (record.moderation_status === 'pending') {
    modPill = <Pill tone="neutral">Photos pending</Pill>
  } else {
    // flagged or rejected
    modPill = <Pill tone="red">Photo flagged</Pill>
  }
  return (
    <Space size={6} wrap>
      {modPill}
      {record.is_mlm ? <Pill tone="jaguar">Distributor</Pill> : null}
    </Space>
  )
}

/**
 * Jaguar-Owned — the admin listings queue.
 *
 * A listing never gets an admin UPDATE policy (`docs/superpowers/specs/…§4.9`):
 * decisions run through the business-listing-decide Edge Function from the Show
 * page, so this list is read-only + ShowButton, same shape as
 * class_year_change_requests. The status Tabs set/clear a single `status eq`
 * filter through useTable's own `setFilters` — no separate local state — so the
 * active tab and `syncWithLocation`'s querystring never drift apart.
 *
 * Stat counts are four bounded pageSize:1 queries reading `result.total`
 * (the class_year pattern): PostgREST returns counts without ever shipping rows.
 */
export function BusinessesList() {
  const { tableProps, filters, setFilters, setCurrentPage } = useTable<Business>({
    resource: 'businesses',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'submitted_at', order: 'desc' }] },
    meta: { select: SELECT },
  })
  const onRow = useRowShow('businesses')

  const activeStatusFilter = filters.find(
    (f) => 'field' in f && f.field === 'status' && f.operator === 'eq',
  )
  const activeTab =
    activeStatusFilter && 'value' in activeStatusFilter ? String(activeStatusFilter.value) : 'all'

  // Refine's setFilters never resets the page on its own — without this, an
  // admin on page 2 of "All" who clicks "Pending" lands on offset 10 of a
  // filtered set that may have far fewer rows, and sees an empty table even
  // though the stat strip shows a nonzero pending count.
  function handleTabChange(key: string) {
    setFilters(key === 'all' ? [] : [{ field: 'status', operator: 'eq', value: key }], 'replace')
    setCurrentPage(1)
  }

  // Four bounded pageSize:1 count queries, spelled out (not looped) so the hook
  // call order is statically obvious — same shape as Class Year Changes / Reports.
  const pendingQ = useList<Business>({
    resource: 'businesses',
    filters: [{ field: 'status', operator: 'eq', value: 'pending' }],
    pagination: { pageSize: 1 },
  })
  const changesQ = useList<Business>({
    resource: 'businesses',
    filters: [{ field: 'status', operator: 'eq', value: 'changes_requested' }],
    pagination: { pageSize: 1 },
  })
  const liveQ = useList<Business>({
    resource: 'businesses',
    filters: [{ field: 'status', operator: 'eq', value: 'approved' }],
    pagination: { pageSize: 1 },
  })
  const pausedQ = useList<Business>({
    resource: 'businesses',
    filters: [{ field: 'status', operator: 'eq', value: 'paused' }],
    pagination: { pageSize: 1 },
  })

  const pending = pendingQ.result?.total ?? 0
  const changes = changesQ.result?.total ?? 0
  const live = liveQ.result?.total ?? 0
  const paused = pausedQ.result?.total ?? 0

  const stats: Stat[] = [
    { label: 'Pending review', value: pending.toLocaleString(), icon: Clock, tone: pending > 0 ? 'gold' : 'neutral' },
    {
      label: 'Changes requested',
      value: changes.toLocaleString(),
      icon: RefreshCw,
      tone: changes > 0 ? 'gold' : 'neutral',
    },
    { label: 'Live', value: live.toLocaleString(), icon: BadgeCheck, tone: live > 0 ? 'green' : 'neutral' },
    { label: 'Paused', value: paused.toLocaleString(), icon: Ban, tone: paused > 0 ? 'jaguar' : 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Jaguar-Owned',
        description:
          'Businesses owned by verified MUAA alumni. Review new and edited listings before members can see them — approving publishes the listing and notifies the owner in the app.',
      }}
      stats={stats}
      statColumns={4}
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={STATUS_TABS.map((t) => ({ key: t.key, label: t.label }))}
        style={{ marginBottom: 12 }}
      />
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Business>
              dataIndex="name"
              title="Business"
              render={(_: unknown, record) => (
                <div>
                  <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 13.5, color: brand.ink }}>
                    {record.name}
                  </div>
                  {record.tagline ? (
                    <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub }}>
                      {record.tagline}
                    </div>
                  ) : null}
                </div>
              )}
            />
            <Table.Column<Business>
              dataIndex="owner"
              title="Owner"
              render={(_: unknown, record) => (
                <Space direction="vertical" size={2}>
                  {renderAvatarName(record.owner?.name ?? 'Unknown owner')}
                  <span style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, marginLeft: 40 }}>
                    Class of {record.owner?.class_year ?? '—'}
                  </span>
                </Space>
              )}
            />
            <Table.Column<Business>
              dataIndex="category_slug"
              title="Category · type"
              render={(_: unknown, record) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12.5, color: brand.ink }}>
                  {record.category_slug} · {typeLabel(record.business_type)}
                </span>
              )}
            />
            <Table.Column<Business>
              dataIndex="submitted_at"
              title="Submitted"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Business>
              title="Checks"
              dataIndex="checks"
              render={(_: unknown, record) => <ChecksCell record={record} />}
            />
            <Table.Column<Business>
              dataIndex="status"
              title="Status"
              render={(status: string) => <Pill tone={statusTone(status)}>{statusLabel(status)}</Pill>}
            />
            <Table.Column<Business>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
